/**
 * Les quelques appels directs à l'API du fournisseur d'identité dont la
 * connexion par Touch ID a besoin. Pas de SDK AWS : six requêtes JSON.
 *
 * Aucun message d'erreur défini ici ne nomme l'infrastructure (doctrine :
 * pas de fuite d'architecture dans la GUI). Le détail va dans la console.
 */

import { getRuntimeConfig } from "@/runtimeConfig";

/** Erreur renvoyée par l'API ; `code` est le nom de l'exception (NotAuthorizedException…). */
export class CognitoIdpError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = "CognitoIdpError";
    this.code = code;
  }
}

export interface AuthenticationResult {
  AccessToken: string;
  IdToken: string;
  ExpiresIn: number;
  TokenType: string;
  /** Renvoyé mais volontairement ignoré : la session Touch ID n'a pas de refresh. */
  RefreshToken?: string;
}

export interface PasskeyDescription {
  CredentialId: string;
  FriendlyCredentialName: string;
  RelyingPartyId: string;
  /** Secondes depuis l'époque Unix. */
  CreatedAt: number;
  AuthenticatorAttachment?: string;
}

interface ChallengeResponse {
  ChallengeName?: string;
  Session?: string;
  ChallengeParameters?: Record<string, string>;
  AuthenticationResult?: AuthenticationResult;
}

async function call<T>(operation: string, body: Record<string, unknown>): Promise<T> {
  const { region } = getRuntimeConfig().cognito;
  const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": `AWSCognitoIdentityProviderService.${operation}`,
    },
    body: JSON.stringify(body),
  });
  const text = await response.text();
  let parsed: Record<string, unknown> = {};
  if (text) {
    try {
      parsed = JSON.parse(text) as Record<string, unknown>;
    } catch {
      // Corps non JSON : on rend le statut HTTP ci-dessous.
    }
  }
  if (!response.ok) {
    const type = typeof parsed.__type === "string" ? parsed.__type : "UnknownError";
    const message =
      typeof parsed.message === "string" ? parsed.message : `HTTP ${response.status}`;
    throw new CognitoIdpError(type.split("#").pop() ?? type, message);
  }
  return parsed as T;
}

/**
 * Ouvre le défi Touch ID pour `username`. Rend les options de la requête
 * WebAuthn (déjà parsées) et la session à rejouer dans `respondToPasskeyChallenge`.
 */
export async function initiatePasskeySignIn(
  username: string,
): Promise<{ session: string; requestOptions: unknown }> {
  const { userPoolClientId } = getRuntimeConfig().cognito;
  const res = await call<ChallengeResponse>("InitiateAuth", {
    AuthFlow: "USER_AUTH",
    ClientId: userPoolClientId,
    AuthParameters: { USERNAME: username, PREFERRED_CHALLENGE: "WEB_AUTHN" },
  });
  if (res.ChallengeName !== "WEB_AUTHN") {
    console.error("Unexpected first challenge", res.ChallengeName);
    throw new CognitoIdpError(
      "PasskeyNotAvailable",
      "Touch ID is not set up for this account. Use your password instead.",
    );
  }
  const raw = res.ChallengeParameters?.CREDENTIAL_REQUEST_OPTIONS;
  if (!raw || !res.Session) {
    console.error("WEB_AUTHN challenge without options", Object.keys(res.ChallengeParameters ?? {}));
    throw new CognitoIdpError(
      "UnexpectedResponse",
      "Sign-in failed: unexpected response from the identity service.",
    );
  }
  return { session: res.Session, requestOptions: JSON.parse(raw) };
}

/** Répond au défi avec l'`AuthenticationResponseJSON` produit par le navigateur. */
export async function respondToPasskeyChallenge(args: {
  username: string;
  session: string;
  credential: unknown;
}): Promise<AuthenticationResult> {
  const { userPoolClientId } = getRuntimeConfig().cognito;
  const res = await call<ChallengeResponse>("RespondToAuthChallenge", {
    ChallengeName: "WEB_AUTHN",
    ClientId: userPoolClientId,
    Session: args.session,
    ChallengeResponses: {
      USERNAME: args.username,
      CREDENTIAL: JSON.stringify(args.credential),
    },
  });
  if (!res.AuthenticationResult) {
    // Un défi de plus (typiquement un code TOTP) : Touch ID ne compte alors pas
    // comme MFA dans ce pool. Le nom du défi est dans la console pour le diagnostic.
    console.error("Extra challenge after Touch ID", res.ChallengeName);
    throw new CognitoIdpError(
      "UnexpectedChallenge",
      "Sign-in needs an extra step that this screen does not support. Use your password instead.",
    );
  }
  return res.AuthenticationResult;
}

/** Options de création d'un passkey pour l'utilisateur du jeton (scope admin requis). */
export async function startPasskeyRegistration(accessToken: string): Promise<unknown> {
  const res = await call<{ CredentialCreationOptions: unknown }>("StartWebAuthnRegistration", {
    AccessToken: accessToken,
  });
  return res.CredentialCreationOptions;
}

/** Enregistre le passkey créé par le navigateur (scope admin requis). */
export async function completePasskeyRegistration(
  accessToken: string,
  credential: unknown,
): Promise<void> {
  await call<Record<string, never>>("CompleteWebAuthnRegistration", {
    AccessToken: accessToken,
    Credential: credential,
  });
}

/**
 * Les passkeys de l'utilisateur (scope admin requis). Un utilisateur en a 20 au
 * plus et `MaxResults` plafonne à 20 : une seule page, pas de pagination.
 */
export async function listPasskeys(accessToken: string): Promise<PasskeyDescription[]> {
  const res = await call<{ Credentials?: PasskeyDescription[] }>("ListWebAuthnCredentials", {
    AccessToken: accessToken,
    MaxResults: 20,
  });
  return res.Credentials ?? [];
}

/** Retire un passkey (scope admin requis). */
export async function deletePasskey(accessToken: string, credentialId: string): Promise<void> {
  await call<Record<string, never>>("DeleteWebAuthnCredential", {
    AccessToken: accessToken,
    CredentialId: credentialId,
  });
}
